import { useFormContext } from 'react-hook-form';
import { RefObject, useCallback, useEffect, useState } from 'react';
import { lt } from '@mews/framework';
import {
    Badge,
    Button,
    EmptyState,
    EmptyStateProps,
    SearchInput,
    Stack,
    Tab,
    Table,
    TableProvider,
    Tabs,
    Typography,
    useTableState,
} from '@optimus-web/core';
import { TextKey, DataTestId } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { companiesCountPerPage, useCompaniesSuspenseQuery } from '../queries';
import { CompaniesTabs } from '../../../../../types';
import { CompaniesFormFields, CompaniesFormValues } from '../../../../../schemas';
import { getCompanySearchMessage, getSelectedCompaniesCount } from '../../../../../utils/companies';
import { RoutingRulesFormFields } from '../../../utils';
import { CompanyWithAdditionalProperties, useCompaniesTableColumns } from './hooks/useCompaniesTableColumns';
import { CompanyChipsList } from './CompanyChipsList';

interface CompaniesListProps {
    contentRef: RefObject<HTMLDivElement>;
}
export const CompaniesList = ({ contentRef }: CompaniesListProps) => {
    const [tableState, setTableState] = useTableState<CompanyWithAdditionalProperties>({ pagination: { show: false } });
    const { watch, setValue } = useFormContext();
    const [searchValue, setSearchValue] = useState<string>('');
    const [searchToken, setSearchToken] = useState<string>('');

    const enterpriseId = useCurrentEnterpriseId();
    const [activeTab, setActiveTab] = useState<CompaniesTabs>(CompaniesTabs.AllProfiles);

    const clearSearch = () => {
        setSearchValue('');
        setSearchToken('');
    };

    const {
        data: companiesData = [],
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
    } = useCompaniesSuspenseQuery({
        searchToken,
        enterpriseId,
        enabled: activeTab === CompaniesTabs.AllProfiles,
    });

    const selectedCompanies: CompaniesFormValues = watch(RoutingRulesFormFields.companies());

    const getAreAllSelected = (key: CompaniesFormFields.LinkAsCompany | CompaniesFormFields.LinkAsTravelAgency) => {
        if (key === CompaniesFormFields.LinkAsCompany) {
            return (
                companiesData.length > 0 &&
                companiesData.every((company) => selectedCompanies[company.id]?.linkAsCompany)
            );
        }

        return (
            companiesData.filter((company) => company.travelAgencyContract).length > 0 &&
            companiesData
                .filter((company) => company.travelAgencyContract)
                .every((company) => selectedCompanies[company.id]?.linkAsTravelAgency)
        );
    };

    const toggleAllSelected = (
        checked: boolean,
        key: CompaniesFormFields.LinkAsCompany | CompaniesFormFields.LinkAsTravelAgency
    ) => {
        const updatedCompanies = { ...selectedCompanies };

        companiesData.forEach((company) => {
            if (key === CompaniesFormFields.LinkAsCompany || company.travelAgencyContract != null) {
                updatedCompanies[company.id] = {
                    ...updatedCompanies[company.id],
                    ...(updatedCompanies[company.id]?.name == null && { name: company.name }),
                    [key]: checked,
                };
            }
        });

        setValue(RoutingRulesFormFields.companies(), updatedCompanies);
    };

    const toggleSelected = ({
        checked,
        id,
        key,
    }: {
        checked: boolean;
        id: string;
        key: CompaniesFormFields.LinkAsCompany | CompaniesFormFields.LinkAsTravelAgency;
    }) => {
        setValue(RoutingRulesFormFields.companies(), {
            ...selectedCompanies,
            [id]: {
                ...selectedCompanies[id],
                name: companiesData.find((company) => company.id === id)?.name,
                [key]: checked,
            },
        });
    };

    const handleCompaniesScroll = useCallback(() => {
        if (!contentRef.current || isFetching || isFetchingNextPage) return;

        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        if (scrollTop + clientHeight + 1 >= scrollHeight && hasNextPage) {
            void fetchNextPage();
        }
    }, [contentRef, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage]);

    useEffect(() => {
        const currentRef = contentRef.current;
        currentRef?.addEventListener('scroll', handleCompaniesScroll);

        return () => {
            currentRef?.removeEventListener('scroll', handleCompaniesScroll);
        };
    }, [contentRef, handleCompaniesScroll]);

    const columns = useCompaniesTableColumns({
        selectedCompanies,
        getAreAllSelected,
        toggleAllSelected,
        toggleSelected,
        companiesData,
    });

    const companiesEmptyState: EmptyStateProps =
        searchToken === ''
            ? {
                  primaryText: lt(TextKey.RoutingRulesFormCompaniesDialogUseSearchMessage),
              }
            : {
                  primaryText: lt(TextKey.RoutingRulesFormCompaniesNoDataTitle),
                  pictogram: 'folder',
                  action: (
                      <Button onClick={clearSearch} type="submit">
                          {lt(TextKey.RoutingRulesFormCompaniesDialogClearSearch)}
                      </Button>
                  ),
              };

    const linkedCompaniesLists =
        getSelectedCompaniesCount(selectedCompanies) === 0 ? (
            <EmptyState
                maxWidth="500px"
                primaryText={lt(TextKey.RoutingRulesFormCompaniesNoSelectedCompaniesMessage)}
                pictogram="system_error"
                action={
                    <Button onClick={() => setActiveTab(CompaniesTabs.AllProfiles)} type="submit">
                        {lt(TextKey.RoutingRulesFormCompaniesDialogAllProfilesEmptyStateButtonText)}
                    </Button>
                }
            />
        ) : (
            <Stack vertical spacing="200">
                <CompanyChipsList
                    selectedCompanies={selectedCompanies}
                    linkType={CompaniesFormFields.LinkAsCompany}
                    searchValue={searchValue}
                    dataTestId={DataTestId.RoutingRulesCompaniesProfiles}
                />
                <CompanyChipsList
                    selectedCompanies={selectedCompanies}
                    linkType={CompaniesFormFields.LinkAsTravelAgency}
                    searchValue={searchValue}
                    dataTestId={DataTestId.RoutingRulesTravelAgenciesProfiles}
                />
            </Stack>
        );

    return (
        <>
            <Tabs
                underline
                inline
                onTabClick={(_, value) => {
                    setActiveTab(value as CompaniesTabs);
                    clearSearch();
                }}
                activeTabValue={activeTab}
            >
                <Tab
                    inline
                    id={CompaniesTabs.AllProfiles}
                    value={CompaniesTabs.AllProfiles}
                    label={lt(TextKey.RoutingRulesFormCompaniesTabAllProfiles)}
                />
                <Tab
                    inline
                    id={CompaniesTabs.LinkedProfiles}
                    value={CompaniesTabs.LinkedProfiles}
                    label={
                        <>
                            {lt(TextKey.RoutingRulesFormCompaniesTabLinkedProfiles)}
                            {UnicodeEntity.NoBreakSpace}

                            <Badge content={getSelectedCompaniesCount(selectedCompanies)} />
                        </>
                    }
                />
            </Tabs>
            <SearchInput
                size="medium"
                name="companiesSearch"
                placeholder={lt(TextKey.SearchByName)}
                value={searchValue}
                onChange={(_, value) => setSearchValue(value.toString())}
                onSearch={(value) => setSearchToken(value.toString())}
                onClear={clearSearch}
            />
            <Typography textStyle="bodySmall" as="div">
                {getCompanySearchMessage({
                    companiesCount: companiesData.length,
                    searchValue,
                    hasNextPage,
                    isFetching,
                    activeTab,
                    selectedCompanies,
                })}
            </Typography>
            {activeTab === CompaniesTabs.AllProfiles ? (
                <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="div">
                    <Table
                        uniqueRowId="id"
                        data={companiesData}
                        amountOfRows={companiesCountPerPage}
                        loading={isFetching}
                        emptyState={{
                            ...companiesEmptyState,
                            maxWidth: '500px',
                        }}
                    />
                </TableProvider>
            ) : (
                linkedCompaniesLists
            )}
        </>
    );
};
